import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Slideshow as SlidesIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  SolutionStory,
  SolutionStoryAdminUpdate,
  solutionStoriesApi,
} from '../services/api';
import {
  STORY_TAB_LABEL,
  STORY_TAB_ORDER,
  StoryTab,
  splitCsv,
} from './solution-stories/storyHelpers';
import { STORY_UI } from './solution-stories/storyUiTokens';
import {
  IconMappings,
  SolutionStorySlidePreview,
} from './solution-stories/SolutionStorySlidePreview';
import {
  SOLUTION_STORY_OPTIONS_QUERY_KEY,
  useSolutionStoryOptions,
} from './solution-stories/useSolutionStoryOptions';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SolutionStoryDetailModalProps {
  open: boolean;
  onClose: () => void;
  story: SolutionStory | null;
  stories?: SolutionStory[];
  currentIndex?: number;
  onNavigate?: (index: number) => void;
  /** 'view' = catalog; 'review' = admin reviewer (edit + approve). */
  mode?: 'view' | 'review';
  onSaved?: (story: SolutionStory) => void;
  onApproved?: (storyId: string) => void;
  onArchived?: (storyId: string) => void;
}

type EditDraft = {
  solution_provided_title: string;
  account_name: string;
  company_details: string;
  business_context_1: string;
  business_context_2: string;
  business_context_3: string;
  business_context_4: string;
  solution_1: string;
  solution_2: string;
  solution_3: string;
  solution_4: string;
  impact_1: string;
  impact_2: string;
  impact_3: string;
  impact_4: string;
  platform: string;
  tech_stack: string;
  industry: string;
  region: string;
  // Deal-win-only card fields
  practice: string;
  winning_strategy_subheading: string;
  win_strategy: string;
  win_wire_story: string;
};

function storyToDraft(story: SolutionStory): EditDraft {
  return {
    solution_provided_title: story.solution_provided_title || '',
    account_name: story.account_name || '',
    company_details: story.company_details || '',
    business_context_1: story.business_context_1 || '',
    business_context_2: story.business_context_2 || '',
    business_context_3: story.business_context_3 || '',
    business_context_4: story.business_context_4 || '',
    solution_1: story.solution_1 || '',
    solution_2: story.solution_2 || '',
    solution_3: story.solution_3 || '',
    solution_4: story.solution_4 || '',
    impact_1: story.impact_1 || '',
    impact_2: story.impact_2 || '',
    impact_3: story.impact_3 || '',
    impact_4: story.impact_4 || '',
    platform: story.platform || '',
    tech_stack: story.tech_stack || '',
    industry: story.industry || '',
    region: story.region || '',
    practice: story.practice || '',
    winning_strategy_subheading: story.winning_strategy_subheading || '',
    win_strategy: story.win_strategy || '',
    win_wire_story: story.win_wire_story || '',
  };
}

/** Compact "Generate Google Slides" control shown in the header (view mode only). */
const HeaderSlidesControl: React.FC<{
  slidesUrl: string | null;
  generatingSlides: boolean;
  onGenerate: () => void;
}> = ({ slidesUrl, generatingSlides, onGenerate }) => {
  if (slidesUrl) {
    return (
      <Box
        component="a"
        id="story-open-slides-btn-header"
        href={slidesUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          backgroundColor: STORY_UI.accent,
          color: '#fff',
          borderRadius: STORY_UI.radiusSm,
          px: '14px',
          py: '8px',
          fontSize: '12.5px',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          textDecoration: 'none',
          fontFamily: STORY_UI.fontSans,
          whiteSpace: 'nowrap',
          '&:hover': { backgroundColor: STORY_UI.accentDeep },
        }}
      >
        <SlidesIcon sx={{ fontSize: 14 }} />
        Open slides
      </Box>
    );
  }

  return (
    <Box
      component="button"
      id="story-generate-slides-btn-header"
      disabled={generatingSlides}
      onClick={onGenerate}
      sx={{
        backgroundColor: generatingSlides ? STORY_UI.line : STORY_UI.accent,
        color: '#fff',
        border: 'none',
        borderRadius: STORY_UI.radiusSm,
        px: '14px',
        py: '8px',
        fontSize: '12.5px',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: generatingSlides ? 'wait' : 'pointer',
        fontFamily: STORY_UI.fontSans,
        whiteSpace: 'nowrap',
        transition: 'background .15s ease',
        '&:hover': { backgroundColor: generatingSlides ? STORY_UI.line : STORY_UI.accentDeep },
      }}
    >
      {generatingSlides ? (
        <CircularProgress size={13} sx={{ color: '#fff' }} />
      ) : (
        <SlidesIcon sx={{ fontSize: 14 }} />
      )}
      {generatingSlides ? 'Generating…' : 'Generate Google Slides'}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Modal — presentation view matching solution-stories.html
// ─────────────────────────────────────────────────────────────────────────────
export const SolutionStoryDetailModal: React.FC<SolutionStoryDetailModalProps> = ({
  open,
  onClose,
  story,
  stories = [],
  currentIndex = -1,
  onNavigate,
  mode = 'view',
  onSaved,
  onApproved,
  onArchived,
}) => {
  const isReview = mode === 'review';
  const [generatingSlides, setGeneratingSlides] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [slidesUrl, setSlidesUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveWithSlides, setApproveWithSlides] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StoryTab>('completed');

  const { data: optionsData } = useQuery({
    queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY,
    queryFn: () => solutionStoriesApi.getOptions(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const { techStackOptions, industryOptions, platformOptions, companyOptions } = useSolutionStoryOptions();
  const iconMappings = optionsData?.icon_mappings;

  useEffect(() => {
    if (!open) {
      setSlidesUrl(null);
      setGenerateError(null);
      setGeneratingSlides(false);
      setEditing(false);
      setDraft(null);
      setSaveError(null);
      setApproving(false);
      setApproveWithSlides(false);
    }
  }, [open]);

  useEffect(() => {
    setSlidesUrl(null);
    setGenerateError(null);
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    if (story) setActiveTab(story.is_deal_win_only ? 'won' : 'completed');
  }, [story?.solution_story_id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editing) {
          setEditing(false);
          setDraft(null);
          setSaveError(null);
        } else onClose();
      }
      if (editing) return;
      if (e.key === 'ArrowRight' && onNavigate && currentIndex >= 0 && stories.length > 0) {
        onNavigate((currentIndex + 1) % stories.length);
      }
      if (e.key === 'ArrowLeft' && onNavigate && currentIndex >= 0 && stories.length > 0) {
        onNavigate((currentIndex - 1 + stories.length) % stories.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, editing, onClose, onNavigate, currentIndex, stories.length]);

  const handleGenerateSlides = async () => {
    if (!story || generatingSlides) return;
    setGeneratingSlides(true);
    setGenerateError(null);
    setSlidesUrl(null);
    try {
      const res = await solutionStoriesApi.generateSlides([story.solution_story_id]);
      if (res.presentation_url) {
        setSlidesUrl(res.presentation_url);
      } else {
        setGenerateError('Slides were created but no open link was returned');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to generate Google Slides';
      setGenerateError(typeof detail === 'string' ? detail : 'Failed to generate Google Slides');
    } finally {
      setGeneratingSlides(false);
    }
  };

  const startEditing = () => {
    if (!story) return;
    setDraft(storyToDraft(story));
    setEditing(true);
    setSaveError(null);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
  };

  const saveEditing = async () => {
    if (!story || !draft || saving) return;
    const title = draft.solution_provided_title.trim();
    if (!title) {
      setSaveError('Title cannot be empty');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: SolutionStoryAdminUpdate = story.is_deal_win_only
        ? {
            solution_provided_title: title,
            account_name: draft.account_name,
            region: draft.region,
            practice: draft.practice,
            business_context_1: draft.business_context_1,
            winning_strategy_subheading: draft.winning_strategy_subheading,
            win_strategy: draft.win_strategy,
            win_wire_story: draft.win_wire_story,
          }
        : {
            solution_provided_title: title,
            account_name: draft.account_name,
            company_details: draft.company_details,
            business_context_1: draft.business_context_1,
            business_context_2: draft.business_context_2,
            business_context_3: draft.business_context_3,
            business_context_4: draft.business_context_4,
            solution_1: draft.solution_1,
            solution_2: draft.solution_2,
            solution_3: draft.solution_3,
            solution_4: draft.solution_4,
            impact_1: draft.impact_1,
            impact_2: draft.impact_2,
            impact_3: draft.impact_3,
            impact_4: draft.impact_4,
            platform: draft.platform,
            tech_stack: draft.tech_stack,
            industry: draft.industry,
            region: draft.region,
          };
      const updated = await solutionStoriesApi.updateAdminStory(story.solution_story_id, payload);
      setEditing(false);
      setDraft(null);
      onSaved?.(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save changes';
      setSaveError(typeof detail === 'string' ? detail : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!story || approving || editing) return;
    setApproving(true);
    setGenerateError(null);
    setSlidesUrl(null);
    try {
      const res = await solutionStoriesApi.approveStory(
        story.solution_story_id,
        approveWithSlides
      );
      onApproved?.(story.solution_story_id);
      if (approveWithSlides) {
        if (res.presentation_url) {
          setSlidesUrl(res.presentation_url);
        } else {
          setGenerateError('Slides were created but no open link was returned');
        }
      } else {
        onClose();
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to approve story';
      setGenerateError(typeof detail === 'string' ? detail : 'Failed to approve story');
    } finally {
      setApproving(false);
    }
  };

  const handleArchive = async () => {
    if (!story || archiving || editing) return;
    if (!window.confirm('Archive this story? It will no longer appear on the public Solution Stories page.')) {
      return;
    }
    setArchiving(true);
    setArchiveError(null);
    try {
      await solutionStoriesApi.archiveStory(story.solution_story_id);
      onArchived?.(story.solution_story_id);
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to archive story';
      setArchiveError(typeof detail === 'string' ? detail : 'Failed to archive story');
    } finally {
      setArchiving(false);
    }
  };

  const setDraftField = (key: keyof EditDraft, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const paraSx = {
    fontSize: '12.5px',
    lineHeight: 1.65,
    color: STORY_UI.inkSoft,
    m: 0,
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '13px',
      fontFamily: STORY_UI.fontSans,
      backgroundColor: '#fff',
    },
    '& .MuiInputLabel-root': { fontSize: '13px' },
  };

  const pointFields = (
    prefix: 'business_context' | 'solution' | 'impact',
    label: string,
    values: [string, string, string, string]
  ) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          color: STORY_UI.inkFaint,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </Typography>
      {([1, 2, 3, 4] as const).map((n) => (
        <TextField
          key={`${prefix}_${n}`}
          size="small"
          multiline
          minRows={2}
          label={`Point ${n}`}
          value={values[n - 1]}
          onChange={(e) => setDraftField(`${prefix}_${n}` as keyof EditDraft, e.target.value)}
          sx={fieldSx}
        />
      ))}
    </Box>
  );

  const step = (delta: number) => {
    if (editing) return;
    if (!onNavigate || currentIndex < 0 || stories.length === 0) return;
    onNavigate((currentIndex + delta + stories.length) % stories.length);
  };

  if (!open || !story) return null;

  return (
    <>
      {/* Scrim + presentation panel */}
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 'var(--dashboard-sidebar-width, 64px)',
          backgroundColor: 'rgba(0,10,30,0.55)',
          backdropFilter: 'blur(2px)',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1.5, sm: 3.5 },
          fontFamily: STORY_UI.fontSans,
        }}
      >
        {/* Prev */}
        {stories.length > 1 && (
          <IconButton
            id="story-pv-prev"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 22 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: 46,
              height: 46,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              display: { xs: 'none', md: 'flex' },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.24)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '100%',
            maxWidth: 1180,
            maxHeight: '100%',
            backgroundColor: STORY_UI.surface,
            borderRadius: '22px',
            overflow: 'hidden',
            boxShadow: STORY_UI.shadowPop,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {/* Customer Journey timeline — doubles as the presentation tab switcher */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                backgroundColor: STORY_UI.surface,
                borderBottom: `1px solid ${STORY_UI.line}`,
                px: { xs: 2, sm: '30px' },
                py: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.25, sm: 3.25 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 1.5, sm: 3.25 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    WebkitOverflowScrolling: 'touch',
                    // Hide the scrollbar on mobile so it reads as a plain tab row, not a scroll widget.
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {STORY_TAB_ORDER.filter((tab) => {
                    // Standalone deal-win cards have no data behind the other tabs.
                    if (story.is_deal_win_only) return tab === 'won';
                    if (tab === 'delivery') {
                      return !!(
                        story.key_objectives_challenges ||
                        story.challenges_changed_since_inception ||
                        story.tech_usage_way ||
                        story.platform_usage_way
                      );
                    }
                    if (tab === 'won') return !!story.win_strategy;
                    return true;
                  }).map((tab) => {
                    const isActive = !editing && activeTab === tab;
                    return (
                      <Box
                        key={tab}
                        component="button"
                        id={`story-tab-${tab}`}
                        onClick={() => !editing && setActiveTab(tab)}
                        title={`Show ${STORY_TAB_LABEL[tab]}`}
                        disabled={editing}
                        sx={{
                          position: 'relative',
                          background: 'none',
                          border: 'none',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          px: '4px',
                          py: '18px',
                          mr: 3,
                          fontSize: '13.5px',
                          fontWeight: 600,
                          color: isActive ? STORY_UI.ink : STORY_UI.inkFaint,
                          cursor: editing ? 'default' : 'pointer',
                          fontFamily: STORY_UI.fontSans,
                          transition: 'color .15s ease',
                          '&:hover': !editing ? { color: isActive ? STORY_UI.ink : STORY_UI.inkSoft } : undefined,
                          '&::after': isActive
                            ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: '-1px',
                                height: '2px',
                                borderRadius: '2px',
                                backgroundColor: STORY_UI.accent,
                              }
                            : undefined,
                        }}
                      >
                        {STORY_TAB_LABEL[tab]}
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {isReview && !editing && (
                    <IconButton
                      id="story-edit-btn"
                      onClick={startEditing}
                      title="Edit story fields"
                      sx={{
                        width: 34,
                        height: 34,
                        backgroundColor: STORY_UI.surfaceSunken,
                        color: STORY_UI.inkSoft,
                        '&:hover': { backgroundColor: STORY_UI.accentTint, color: STORY_UI.accentDeep },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  {isReview && !editing && !story.is_deal_win_only && (
                    <IconButton
                      id="story-archive-btn"
                      onClick={handleArchive}
                      disabled={archiving}
                      title="Archive story"
                      sx={{
                        width: 34,
                        height: 34,
                        backgroundColor: STORY_UI.surfaceSunken,
                        color: STORY_UI.inkSoft,
                        '&:hover': { backgroundColor: '#fdecea', color: '#c00000' },
                      }}
                    >
                      {archiving ? (
                        <CircularProgress size={16} />
                      ) : (
                        <ArchiveIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  )}
                  {isReview && editing && (
                    <>
                      <Button
                        id="story-edit-save-btn"
                        size="small"
                        disabled={saving}
                        startIcon={
                          saving ? (
                            <CircularProgress size={12} />
                          ) : (
                            <SaveIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        onClick={saveEditing}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12.5px',
                          borderRadius: STORY_UI.radiusSm,
                          backgroundColor: STORY_UI.accent,
                          color: '#fff',
                          px: 1.5,
                          '&:hover': { backgroundColor: STORY_UI.accentDeep },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        id="story-edit-cancel-btn"
                        size="small"
                        disabled={saving}
                        onClick={cancelEditing}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '12.5px',
                          borderRadius: STORY_UI.radiusSm,
                          color: STORY_UI.inkSoft,
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {!isReview && !editing && activeTab === 'completed' && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                      <HeaderSlidesControl
                        slidesUrl={slidesUrl}
                        generatingSlides={generatingSlides}
                        onGenerate={handleGenerateSlides}
                      />
                    </Box>
                  )}
                  <IconButton
                    id="solution-story-detail-close-btn"
                    onClick={onClose}
                    title="Close"
                    sx={{
                      width: 34,
                      height: 34,
                      backgroundColor: STORY_UI.surfaceSunken,
                      color: STORY_UI.inkSoft,
                      '&:hover': { backgroundColor: STORY_UI.accentTint, color: STORY_UI.accentDeep },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Mobile: the slides control drops below the tab row instead of squeezing into it */}
              {!isReview && !editing && activeTab === 'completed' && (
                <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                  <HeaderSlidesControl
                    slidesUrl={slidesUrl}
                    generatingSlides={generatingSlides}
                    onGenerate={handleGenerateSlides}
                  />
                </Box>
              )}
              {!isReview && generateError && (
                <Typography sx={{ fontSize: '12.5px', color: '#c00000' }}>{generateError}</Typography>
              )}
              {isReview && archiveError && (
                <Typography sx={{ fontSize: '12.5px', color: '#c00000' }}>{archiveError}</Typography>
              )}
            </Box>

            {editing && draft ? (
              <Box
                sx={{
                  px: { xs: 2, sm: '34px' },
                  py: '22px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  borderBottom: `1px solid ${STORY_UI.line}`,
                  backgroundColor: STORY_UI.surfaceSunken,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: STORY_UI.fontDisplay,
                      fontWeight: 600,
                      fontSize: '18px',
                      color: STORY_UI.ink,
                      mb: 0.5,
                    }}
                  >
                    Edit story
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: STORY_UI.inkSoft }}>
                    Update the fields below, then save. The slide preview will refresh after you save.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={draft.solution_provided_title}
                  onChange={(e) => setDraftField('solution_provided_title', e.target.value)}
                  inputProps={{ maxLength: 70 }}
                  helperText={`${draft.solution_provided_title.length}/70`}
                  sx={fieldSx}
                />

                <Autocomplete
                  freeSolo
                  size="small"
                  options={companyOptions}
                  value={draft.account_name}
                  onInputChange={(_e, newValue) => setDraftField('account_name', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="Company Name" sx={fieldSx} />
                  )}
                />

                {!story.is_deal_win_only && (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    label="Company Details"
                    value={draft.company_details}
                    onChange={(e) => setDraftField('company_details', e.target.value)}
                    sx={fieldSx}
                  />
                )}

                {story.is_deal_win_only && (
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    label="Subheading"
                    value={draft.winning_strategy_subheading}
                    onChange={(e) => setDraftField('winning_strategy_subheading', e.target.value)}
                    sx={fieldSx}
                  />
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: story.is_deal_win_only ? '1fr' : '1fr 1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {pointFields('business_context', 'Business Context', [
                    draft.business_context_1,
                    draft.business_context_2,
                    draft.business_context_3,
                    draft.business_context_4,
                  ])}
                  {!story.is_deal_win_only && pointFields('solution', 'Solution', [
                    draft.solution_1,
                    draft.solution_2,
                    draft.solution_3,
                    draft.solution_4,
                  ])}
                  {!story.is_deal_win_only && pointFields('impact', 'Impact', [
                    draft.impact_1,
                    draft.impact_2,
                    draft.impact_3,
                    draft.impact_4,
                  ])}
                </Box>

                {story.is_deal_win_only && (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      label="Win Strategy"
                      value={draft.win_strategy}
                      onChange={(e) => setDraftField('win_strategy', e.target.value)}
                      sx={fieldSx}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={4}
                      label="Win Wire Story"
                      value={draft.win_wire_story}
                      onChange={(e) => setDraftField('win_wire_story', e.target.value)}
                      sx={fieldSx}
                    />
                  </>
                )}

                {!story.is_deal_win_only && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <FormControl size="small" sx={fieldSx}>
                    <InputLabel id="review-platform-label">Platform</InputLabel>
                    <Select
                      labelId="review-platform-label"
                      label="Platform"
                      multiple
                      value={splitCsv(draft.platform)}
                      onChange={(e) => {
                        const value = e.target.value;
                        const next = (typeof value === 'string' ? value.split(',') : value).slice(0, 3);
                        setDraftField('platform', next.join(', '));
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {platformOptions.map((p) => {
                        const selected = splitCsv(draft.platform);
                        const isSelected = selected.includes(p);
                        const atMax = selected.length >= 3;
                        return (
                          <MenuItem key={p} value={p} disabled={!isSelected && atMax}>
                            {p}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={techStackOptions}
                    value={
                      draft.tech_stack
                        ? draft.tech_stack.split(',').map((s) => s.trim()).filter(Boolean)
                        : []
                    }
                    onChange={(_e, newValue) =>
                      setDraftField(
                        'tech_stack',
                        (newValue as string[]).map((s) => s.trim()).filter(Boolean).join(', ')
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          size="small"
                          label={option}
                          {...getTagProps({ index })}
                          key={`${option}-${index}`}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tech Stack"
                        helperText="Select from configured options or type to add"
                        sx={fieldSx}
                      />
                    )}
                  />
                </Box>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {story.is_deal_win_only ? (
                    <TextField
                      size="small"
                      label="Practice"
                      value={draft.practice}
                      onChange={(e) => setDraftField('practice', e.target.value)}
                      sx={fieldSx}
                    />
                  ) : (
                  <FormControl size="small" sx={fieldSx}>
                    <InputLabel id="review-industry-label">Industry</InputLabel>
                    <Select
                      labelId="review-industry-label"
                      label="Industry"
                      value={draft.industry}
                      onChange={(e) => setDraftField('industry', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select industry</em>
                      </MenuItem>
                      {industryOptions.map((industry) => (
                        <MenuItem key={industry} value={industry}>
                          {industry}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  )}
                  <TextField
                    size="small"
                    label="Region"
                    value={draft.region}
                    onChange={(e) => setDraftField('region', e.target.value)}
                    sx={fieldSx}
                  />
                </Box>

                {saveError && (
                  <Typography sx={{ fontSize: '12.5px', color: '#c00000' }}>{saveError}</Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', pt: 0.5 }}>
                  <Button
                    id="story-edit-form-cancel-btn"
                    size="small"
                    disabled={saving}
                    onClick={cancelEditing}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '13px',
                      borderRadius: STORY_UI.radiusSm,
                      color: STORY_UI.inkSoft,
                      px: 2,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="story-edit-form-save-btn"
                    size="small"
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={12} />
                      ) : (
                        <SaveIcon sx={{ fontSize: 14 }} />
                      )
                    }
                    onClick={saveEditing}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      borderRadius: STORY_UI.radiusSm,
                      backgroundColor: STORY_UI.accent,
                      color: '#fff',
                      px: 2,
                      '&:hover': { backgroundColor: STORY_UI.accentDeep },
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <SolutionStorySlidePreview
                story={story}
                iconMappings={iconMappings as IconMappings | undefined}
                loadIconMappings={false}
                activeTab={activeTab}
              />
            )}

            {/* Actions (review mode only — the view-mode Generate Slides control lives in the header) */}
            {!editing && isReview && (
            <Box
              sx={{
                px: { xs: 2, sm: '34px' },
                pt: '22px',
                pb: '28px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.25,
                alignItems: 'center',
              }}
            >
              {saveError && (
                <Typography sx={{ width: '100%', fontSize: '12.5px', color: '#c00000' }}>
                  {saveError}
                </Typography>
              )}
              {slidesUrl ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.25,
                    backgroundColor: 'rgba(0,100,255,0.08)',
                    border: `1px solid ${STORY_UI.accent}`,
                    borderRadius: STORY_UI.radiusSm,
                    px: '16px',
                    py: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13.5px',
                      fontWeight: 600,
                      color: STORY_UI.ink,
                      fontFamily: STORY_UI.fontSans,
                    }}
                  >
                    Slides have been created
                  </Typography>
                  <Box
                    component="a"
                    id="story-open-slides-btn"
                    href={slidesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: STORY_UI.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: STORY_UI.radiusSm,
                      px: '16px',
                      py: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      textDecoration: 'none',
                      fontFamily: STORY_UI.fontSans,
                      boxShadow: '0 4px 14px -4px rgba(0,100,255,0.45)',
                      transition: 'background .15s ease, transform .1s ease',
                      '&:hover': {
                        backgroundColor: STORY_UI.accentDeep,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <SlidesIcon sx={{ fontSize: 14 }} />
                    Open now
                  </Box>
                </Box>
              ) : (
                <>
                  {!story.is_deal_win_only && (
                  <FormControlLabel
                    sx={{ width: '100%', m: 0 }}
                    control={
                      <Checkbox
                        id="story-approve-create-slides-checkbox"
                        checked={approveWithSlides}
                        onChange={(e) => setApproveWithSlides(e.target.checked)}
                        disabled={approving || generatingSlides || editing}
                        sx={{
                          color: STORY_UI.inkFaint,
                          '&.Mui-checked': { color: STORY_UI.accent },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '13px', color: STORY_UI.inkSoft }}>
                        Create Google Slides and send notification email on approval
                      </Typography>
                    }
                  />
                  )}
                  {!story.is_deal_win_only && (
                  <Box
                    component="button"
                    id="story-generate-slides-btn"
                    disabled={generatingSlides || approving || editing}
                    onClick={handleGenerateSlides}
                    sx={{
                      backgroundColor:
                        generatingSlides || approving || editing ? STORY_UI.line : STORY_UI.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: STORY_UI.radiusSm,
                      px: '22px',
                      py: '13px',
                      fontSize: '14px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor:
                        generatingSlides || approving || editing ? 'not-allowed' : 'pointer',
                      fontFamily: STORY_UI.fontSans,
                      boxShadow:
                        generatingSlides || approving || editing
                          ? 'none'
                          : '0 4px 14px -4px rgba(0,100,255,0.45)',
                      transition: 'background .15s ease, transform .1s ease',
                      '&:hover': {
                        backgroundColor:
                          generatingSlides || approving || editing
                            ? STORY_UI.line
                            : STORY_UI.accentDeep,
                        transform:
                          generatingSlides || approving || editing ? 'none' : 'translateY(-1px)',
                      },
                    }}
                  >
                    {generatingSlides ? (
                      <CircularProgress size={15} sx={{ color: '#fff' }} />
                    ) : (
                      <SlidesIcon sx={{ fontSize: 15 }} />
                    )}
                    {generatingSlides ? 'Generating…' : 'Generate Google Slides'}
                  </Box>
                  )}
                  <Box
                    component="button"
                    id="story-approve-create-slides-btn"
                    disabled={approving || generatingSlides || editing}
                    onClick={handleApprove}
                    sx={{
                      backgroundColor:
                        approving || generatingSlides || editing ? STORY_UI.line : STORY_UI.navy2,
                      color: '#fff',
                      border: 'none',
                      borderRadius: STORY_UI.radiusSm,
                      px: '22px',
                      py: '13px',
                      fontSize: '14px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor:
                        approving || generatingSlides || editing ? 'not-allowed' : 'pointer',
                      fontFamily: STORY_UI.fontSans,
                      boxShadow:
                        approving || generatingSlides || editing
                          ? 'none'
                          : '0 4px 14px -4px rgba(0,53,131,0.45)',
                      transition: 'background .15s ease, transform .1s ease',
                      '&:hover': {
                        backgroundColor:
                          approving || generatingSlides || editing
                            ? STORY_UI.line
                            : STORY_UI.navy,
                        transform:
                          approving || generatingSlides || editing ? 'none' : 'translateY(-1px)',
                      },
                    }}
                  >
                    {approving ? (
                      <CircularProgress size={15} sx={{ color: '#fff' }} />
                    ) : (
                      <CheckIcon sx={{ fontSize: 15 }} />
                    )}
                    {approving ? 'Approving…' : 'Approve'}
                  </Box>
                </>
              )}
              {generateError && (
                <Typography sx={{ width: '100%', fontSize: '12.5px', color: '#c00000' }}>
                  {generateError}
                </Typography>
              )}
            </Box>
            )}
          </Box>

          {/* Mobile-only bottom nav bar: the side prev/next arrows have no room next to a full-width modal */}
          {stories.length > 1 && currentIndex >= 0 && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderTop: `1px solid ${STORY_UI.line}`,
                backgroundColor: STORY_UI.surface,
                flexShrink: 0,
              }}
            >
              <IconButton
                id="story-pv-prev-mobile"
                onClick={(e) => {
                  e.stopPropagation();
                  step(-1);
                }}
                size="small"
                sx={{ color: STORY_UI.inkSoft }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: '12px', color: STORY_UI.inkSoft, fontFamily: STORY_UI.fontSans }}>
                {currentIndex + 1} / {stories.length}
              </Typography>
              <IconButton
                id="story-pv-next-mobile"
                onClick={(e) => {
                  e.stopPropagation();
                  step(1);
                }}
                size="small"
                sx={{ color: STORY_UI.inkSoft }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Next */}
        {stories.length > 1 && (
          <IconButton
            id="story-pv-next"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 22 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: 46,
              height: 46,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              display: { xs: 'none', md: 'flex' },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.24)' },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        )}
      </Box>
    </>
  );
};

export default SolutionStoryDetailModal;
